import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, X, Eye, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShops } from '../services/shopService';
import { fetchShopProducts } from '../services/shopProductService';
import { fetchAvailabilityRules } from '../services/availabilityRuleService';
import {
  fetchMenuSections,
  createMenuSection,
  updateMenuSection,
  deleteMenuSection,
  fetchMenuSectionProducts,
  updateMenuSectionProducts,
} from '../services/menuSectionService';

const emptyForm = {
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
  productIds: [],
  availabilityRuleIds: [],
};

const toStringId = (value) => String(value);

const getProductDisplayName = (product) => {
  if (!product) return 'Unknown product';
  if (product.customName) return product.customName;
  if (product.product?.name) return product.product.name;
  return `Product #${product.id}`;
};

const formatAvailabilityRule = (rule) => {
  if (!rule) return 'No availability';
  const dayList = Array.isArray(rule.days) ? rule.days : [];
  const days = dayList.join(', ').toUpperCase();
  return `${rule.name} (${days || 'ALL DAYS'} ${rule.startTime?.slice(0, 5)}-${rule.endTime?.slice(0, 5)})`;
};

export default function MenuSections() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingSection, setEditingSection] = useState(null);
  const [productDrawer, setProductDrawer] = useState({
    open: false,
    section: null,
    selected: [],
    loading: false,
    saving: false,
  });
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [drawerSearchTerm, setDrawerSearchTerm] = useState('');

  useEffect(() => {
    fetchShops({ limit: 200 })
      .then((res) => {
        const records = res.data?.data?.records || res.data?.data || [];
        setShops(records);
        if (records.length) {
          setShopId(String(records[0].id));
        }
      })
      .catch(() => toast.error('Failed to load shops'));

    fetchAvailabilityRules({ limit: 200 })
      .then((res) => {
        const records = res.data?.data?.records || res.data?.data || [];
        setAvailabilityRules(records.filter((rule) => rule.isActive !== false));
      })
      .catch(() => toast.error('Failed to load availability rules'));
  }, []);

  useEffect(() => {
    if (!shopId) return;
    loadSections();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const loadSections = async () => {
    if (!shopId) return;
    try {
      setLoading(true);
      const res = await fetchMenuSections(shopId);
      setSections(res.data?.data || []);
    } catch (error) {
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    if (!shopId) return;
    try {
      const res = await fetchShopProducts(shopId, { limit: 200 });
      const records = res.data?.data?.records || res.data?.data || [];
      setProducts(records);
    } catch (error) {
      toast.error('Failed to load shop products');
    }
  };

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        id: toStringId(product.id),
        label: getProductDisplayName(product),
      })),
    [products],
  );

  const filteredFormProductOptions = useMemo(() => {
    const query = productSearchTerm.trim().toLowerCase();
    if (!query) return productOptions;
    return productOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [productOptions, productSearchTerm]);

  const filteredDrawerProductOptions = useMemo(() => {
    const query = drawerSearchTerm.trim().toLowerCase();
    if (!query) return productOptions;
    return productOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [productOptions, drawerSearchTerm]);

  const availabilityOptions = useMemo(
    () =>
      availabilityRules.map((rule) => ({
        id: toStringId(rule.id),
        label: formatAvailabilityRule(rule),
      })),
    [availabilityRules],
  );

  const openDrawer = (section = null) => {
    if (section) {
      setEditingSection(section);
      setForm({
        name: section.name || '',
        description: section.description || '',
        sortOrder: section.sortOrder ?? 0,
        isActive: section.isActive !== false,
        productIds: (section.menuSectionProducts || []).map((link) => toStringId(link.shopProductId)),
        availabilityRuleIds: (section.availabilityRules || []).map((rule) => toStringId(rule.id)),
      });
    } else {
      setEditingSection(null);
      setForm(emptyForm);
    }
    setProductSearchTerm('');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingSection(null);
    setForm(emptyForm);
    setProductSearchTerm('');
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleProduct = (id) => {
    setForm((prev) => {
      const stringId = toStringId(id);
      const exists = prev.productIds.includes(stringId);
      return {
        ...prev,
        productIds: exists
          ? prev.productIds.filter((pid) => pid !== stringId)
          : [...prev.productIds, stringId],
      };
    });
  };

  const toggleAvailability = (id) => {
    setForm((prev) => {
      const stringId = toStringId(id);
      const exists = prev.availabilityRuleIds.includes(stringId);
      return {
        ...prev,
        availabilityRuleIds: exists
          ? prev.availabilityRuleIds.filter((rid) => rid !== stringId)
          : [...prev.availabilityRuleIds, stringId],
      };
    });
  };

  const openProductDrawer = (section) => {
    if (!section || !shopId) return;
    loadProducts();
    const initialSelection = (section.menuSectionProducts || []).map((item) =>
      toStringId(item.shopProductId),
    );
    setProductDrawer({
      open: true,
      section,
      selected: initialSelection,
      loading: true,
      saving: false,
    });
    setDrawerSearchTerm('');
    fetchMenuSectionProducts(shopId, section.id)
      .then((res) => {
        const records = res.data?.data || [];
        setProductDrawer((prev) => ({
          ...prev,
          selected: records.map((item) => toStringId(item.shopProductId)),
          loading: false,
        }));
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setProductDrawer((prev) => ({ ...prev, loading: false }));
          return;
        }
        toast.error('Failed to load menu products');
        setProductDrawer((prev) => ({ ...prev, loading: false }));
      });
  };

  const closeProductDrawer = () => {
    setProductDrawer({ open: false, section: null, selected: [], loading: false, saving: false });
  };

  const toggleDrawerProduct = (id) => {
    setProductDrawer((prev) => {
      const stringId = toStringId(id);
      const exists = prev.selected.includes(stringId);
      return {
        ...prev,
        selected: exists
          ? prev.selected.filter((pid) => pid !== stringId)
          : [...prev.selected, stringId],
      };
    });
  };

  const handleProductDrawerSubmit = async () => {
    if (!shopId || !productDrawer.section) {
      toast.error('Select a shop to map products');
      return;
    }
    setProductDrawer((prev) => ({ ...prev, saving: true }));
    try {
      await updateMenuSectionProducts(shopId, productDrawer.section.id, productDrawer.selected);
      toast.success('Menu products updated');
      closeProductDrawer();
      loadSections();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update menu products');
      setProductDrawer((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shopId) {
      toast.error('Select a shop first');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.productIds.length) {
      toast.error('Select at least one product');
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: Boolean(form.isActive),
      productIds: form.productIds,
      availabilityRuleIds: form.availabilityRuleIds,
    };

    try {
      if (editingSection) {
        await updateMenuSection(shopId, editingSection.id, payload);
        toast.success('Menu updated');
      } else {
        await createMenuSection(shopId, payload);
        toast.success('Menu created');
      }
      closeDrawer();
      loadSections();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save menu');
    }
  };

  const handleDelete = async (section) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete menu?',
      text: `Are you sure you want to delete ${section.name}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteMenuSection(shopId, section.id);
      toast.success('Menu deleted');
      loadSections();
    } catch (error) {
      toast.error('Failed to delete menu');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="p-6 space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Menus</h1>
            <p className="text-sm text-gray-500">
              Organise shop products into menus and control when each menu is available to customers.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => openDrawer()}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm shadow hover:bg-indigo-700"
              disabled={!shopId}
            >
              <Plus size={16} />
              Add Menu
            </button>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Products</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Availability</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Sort Order</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      Loading menus...
                    </td>
                  </tr>
                ) : sections.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      No menus found for this shop.
                    </td>
                  </tr>
                ) : (
                  sections.map((section) => (
                    <tr key={section.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{section.name}</div>
                        {section.description && (
                          <div className="text-xs text-gray-500 mt-1">{section.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">
                            {(section.menuSectionProducts?.length || 0).toLocaleString()} product
                            {section.menuSectionProducts?.length === 1 ? '' : 's'}
                          </span>
                          <button
                            type="button"
                            onClick={() => openProductDrawer(section)}
                            className="p-1.5 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                            aria-label="View and map products"
                            title="View and map products"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {section.availabilityRules?.length ? (
                          <ul className="space-y-1 text-xs text-gray-700">
                            {section.availabilityRules.map((rule) => (
                              <li key={rule.id}>{formatAvailabilityRule(rule)}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-xs text-gray-500">Always available</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{section.sortOrder ?? 0}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            section.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {section.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openDrawer(section)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(section)}
                            className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end">
          <div className="w-full max-w-xl bg-white h-full shadow-xl flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingSection ? 'Edit Menu' : 'Create Menu'}
                </h2>
                <p className="text-xs text-gray-500">
                  Assign products to this menu and control when it is visible in the ordering apps.
                </p>
              </div>
              <button type="button" onClick={closeDrawer} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => updateForm('sortOrder', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={form.isActive ? '1' : '0'}
                    onChange={(e) => updateForm('isActive', e.target.value === '1')}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Products</label>
                <input
                  type="text"
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                  {filteredFormProductOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.productIds.includes(option.id)}
                        onChange={() => toggleProduct(option.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                  {!productOptions.length && (
                    <div className="px-3 py-2 text-sm text-gray-500">No products available for this shop.</div>
                  )}
                  {productOptions.length > 0 && !filteredFormProductOptions.length && (
                    <div className="px-3 py-2 text-sm text-gray-500">No products match your search.</div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Availability Rules</label>
                <p className="text-xs text-gray-500 mb-1">
                  Leave empty to keep the section available at all times.
                </p>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                  {availabilityOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.availabilityRuleIds.includes(option.id)}
                        onChange={() => toggleAvailability(option.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                  {!availabilityOptions.length && (
                    <div className="px-3 py-2 text-sm text-gray-500">No availability rules defined.</div>
                  )}
                </div>
              </div>
            </form>
            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                onClick={closeDrawer}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md shadow hover:bg-indigo-700"
              >
                {editingSection ? 'Update Section' : 'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}

      {productDrawer.open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Map Products</h2>
                <p className="text-xs text-gray-500">
                  Choose which products appear under "{productDrawer.section?.name}".
                </p>
              </div>
              <button type="button" onClick={closeProductDrawer} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {productDrawer.loading
                    ? 'Loading products...'
                    : `${productDrawer.selected.length} selected`}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    closeProductDrawer();
                    if (shopId) {
                      navigate(`/shop-products/add?shopId=${shopId}`);
                    } else {
                      navigate('/shop-products/add');
                    }
                  }}
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  + Add Product
                </button>
              </div>
              <input
                type="text"
                value={drawerSearchTerm}
                onChange={(e) => setDrawerSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                aria-label="Search menu products"
              />
              <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {productDrawer.loading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    Loading products...
                  </div>
                ) : filteredDrawerProductOptions.length ? (
                  filteredDrawerProductOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={productDrawer.selected.includes(option.id)}
                        onChange={() => toggleDrawerProduct(option.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))
                ) : !productOptions.length ? (
                  <div className="px-3 py-4 text-sm text-gray-500">No products available for this shop.</div>
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500">No products match your search.</div>
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                onClick={closeProductDrawer}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProductDrawerSubmit}
                disabled={productDrawer.saving || productDrawer.loading}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md shadow hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {productDrawer.saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Mapping'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
